import { useSnackbar } from 'notistack';
import { createContext, PropsWithChildren, useContext } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { OnScreenTextData, TableDataResponse } from 'types/backend';
import { useHttp } from './provide-http';

type OnScreenTextContextType = {
    values?: {
        pdfExportLeadTime?: OnScreenTextData;
        pdfExportFreight?: OnScreenTextData;
        exportPcrPriceChanges?: OnScreenTextData;
        minimumOrderQuantity?: OnScreenTextData;
    };
    isLoading: boolean;
    isFetching: boolean;
    invalidateQuery?: () => void;
    data?: TableDataResponse<OnScreenTextData>;
};

const onScreenTextContext = createContext<OnScreenTextContextType>({ isLoading: false, isFetching: false });

export function ProvideOnScreenText({ children }: PropsWithChildren<{}>) {
    const http = useHttp();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const invalidateQuery = () => queryClient.invalidateQueries('PdfExportVerbiageQuery');
    const { data, isLoading, isFetching } = useQuery<TableDataResponse<OnScreenTextData>, Error>(
        'PdfExportVerbiageQuery',
        async () => {
            const { data } = await http.post(`api/onscreentext/subset`, {
                FilterModel: {
                    Items: [],
                },
                SortModel: [],
            });
            return data || [];
        },
        {
            onError: (error) => enqueueSnackbar(error.message, { variant: 'error' }),
            keepPreviousData: true,
            refetchOnWindowFocus: false,
        }
    );
    const exportLeadTime = data?.rows.find((aRow) => aRow.name.toLowerCase() === 'pdfexportleadtime');
    const exportFreight = data?.rows.find((aRow) => aRow.name.toLowerCase() === 'pdfexportfreight');
    const exportPcrPriceChanges = data?.rows.find(
        (aRow) => aRow.name.toLowerCase() === 'pdfExportPcrPriceChanges'.toLowerCase()
    );
    const minimumOrderQuantity = data?.rows.find((aRow) => aRow.name.toLowerCase() === 'minimumorderquantity');

    const value: OnScreenTextContextType = {
        values: {
            pdfExportLeadTime: exportLeadTime,
            pdfExportFreight: exportFreight,
            exportPcrPriceChanges,
            minimumOrderQuantity,
        },
        isLoading,
        isFetching,
        invalidateQuery,
        data,
    };
    return <onScreenTextContext.Provider value={value}>{children}</onScreenTextContext.Provider>;
}

export function useOnScreenTextContext() {
    return useContext(onScreenTextContext);
}
